// The taxi squad, as a league's rules have it (PLAN-2026-10-27-the-next-season
// §2.7, scenario Q8): who may go there, until when, and the sentences a
// lineup says about it. Sleeper alone has one: a player within the league's
// years of experience goes on from the roster, counts toward no limit and
// never starts, comes off whenever his manager likes, and nobody goes on
// after the deadline, the first kickoff of a week the commissioner sets.
//
// Pure and sport-free: the slot's name is handed in.

/** A league's taxi rule, as a host hands it in (`roster.taxi`, named). */
export interface TaxiRule {
  /** The taxi slot's id: `TAXI`. */
  slot: string;
  /** Its name as the sport says it: `Taxi`. */
  slotName: string;
  count: number;
  /** The most seasons in the pro league a player there may have; 0 is rookies only. */
  maxYearsExperience: number;
  /** The week whose first kickoff closes it; null is the league's first week. */
  deadlinePeriod: number | null;
}

/**
 * The week the squad closes in, inside the regular season (§2.7 rule 4): a
 * week at or before its first is the season's first kickoff, null; one past
 * its last is its last. A schedule moved after the deadline was chosen, or a
 * file's rules brought into another season, leaves the deadline a week the
 * season no longer has, and every reader takes it the same way.
 */
export function taxiDeadlineWeek(
  deadlinePeriod: number | null,
  season: { firstPeriod: number; lastPeriod: number },
): number | null {
  if (deadlinePeriod === null || deadlinePeriod <= season.firstPeriod) return null;
  return Math.min(deadlinePeriod, Math.max(season.firstPeriod, season.lastPeriod));
}

/** One week of a league's calendar, as the deadline reads it. */
export interface TaxiWeek<T> {
  period: number;
  /** A week of the sport's before or after the league's season. */
  off: boolean;
  firstKickoff: T | null;
}

/**
 * When the squad closes (§2.7 rule 4): the first kickoff of the week the
 * rules name, or of the league's first week, read from the first week from
 * there that the league plays and whose kickoff is known. The sport's weeks
 * before a league's season are off, kickoffs and all, and close nothing.
 * Null with no calendar yet. `weeks` in order.
 */
export function taxiClosesAt<T>(
  weeks: readonly TaxiWeek<T>[],
  deadlinePeriod: number | null,
): T | null {
  for (const w of weeks) {
    if (w.off || w.firstKickoff === null) continue;
    if (deadlinePeriod !== null && w.period < deadlinePeriod) continue;
    return w.firstKickoff;
  }
  return null;
}

/**
 * Whether a player with this many seasons in the pro league may go on the
 * taxi squad (§2.7 rule 1). A player whose seasons nobody knows may not.
 */
export function qualifiesForTaxi(
  rule: Pick<TaxiRule, 'maxYearsExperience'>,
  experience: number | null,
): boolean {
  return experience !== null && experience <= rule.maxYearsExperience;
}

/**
 * Whether players may still go onto the taxi squad (§2.7 rule 4): until it
 * closes (`taxiClosesAt`). A league with no calendar yet is before it.
 */
export function taxiOpen(firstKickoff: number | null, at: number): boolean {
  return firstKickoff === null || at < firstKickoff;
}

/** "rookies", "players with 2 or fewer seasons in the league". */
function whoQualifies(rule: Pick<TaxiRule, 'maxYearsExperience'>): string {
  return rule.maxYearsExperience === 0
    ? 'rookies'
    : `players with ${String(rule.maxYearsExperience)} or fewer seasons in the league`;
}

/** "taxi", the slot's name in a sentence. */
function squad(rule: Pick<TaxiRule, 'slotName'>): string {
  return rule.slotName.toLowerCase();
}

/** "the season’s first kickoff", "the first kickoff of Week 4". */
function closes(rule: Pick<TaxiRule, 'deadlinePeriod'>): string {
  return rule.deadlinePeriod === null
    ? 'the season’s first kickoff'
    : `the first kickoff of Week ${String(rule.deadlinePeriod)}`;
}

/** "1 season", "4 seasons". */
function seasons(n: number): string {
  return `${String(n)} ${n === 1 ? 'season' : 'seasons'}`;
}

/**
 * The rule in a sentence, for the lineup: "3 taxi spots for players with 2
 * or fewer seasons in the league, filled until the season's first kickoff. A
 * player there counts toward no limit and never starts."
 */
export function describeTaxi(rule: TaxiRule): string {
  return `${String(rule.count)} ${squad(rule)} ${rule.count === 1 ? 'spot' : 'spots'} for ${whoQualifies(rule)}, filled until ${closes(rule)}. A player there counts toward no limit and never starts.`;
}

/** Why a player cannot go on the taxi squad, or null when he can (§2.7 rules 1 and 4). */
export function taxiRefusal(
  rule: TaxiRule,
  name: string,
  experience: number | null,
  open: boolean,
): string | null {
  if (!open)
    return `The ${squad(rule)} squad closed at ${closes(rule)}: a player can come off it, and nobody goes on.`;
  if (qualifiesForTaxi(rule, experience)) return null;
  return experience === null
    ? `The ${squad(rule)} squad takes ${whoQualifies(rule)}, and ${name}’s seasons in the league are not known.`
    : `The ${squad(rule)} squad takes ${whoQualifies(rule)}. ${name} has ${seasons(experience)}.`;
}
